import { zipSync } from 'fflate';
import type { TriangleMesh } from '../types';
import type { Placement } from './placement';

/**
 * 3MF writer.
 *
 * 3MF is an OPC (zip) package. Unlike STL it records units explicitly and keeps
 * parts as separate, named objects, so a whole wall exports as one file that a
 * slicer still shows as individual panels.
 *
 * The package contains the three parts the core spec requires:
 *   [Content_Types].xml   — media types for the parts
 *   _rels/.rels           — points at the model part
 *   3D/3dmodel.model      — the geometry itself
 */

const MODEL_PATH = '3D/3dmodel.model';

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/>
</Types>
`;

const ROOT_RELS = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rel0" Target="/${MODEL_PATH}" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
</Relationships>
`;

export interface ThreeMfObject {
  name: string;
  mesh: TriangleMesh;
  /** Where the build item puts this object. Recorded, not baked in. */
  placement?: Placement;
}

export interface ThreeMfOptions {
  title?: string;
  designer?: string;
  description?: string;
  application?: string;
}

/** Escapes the five XML predefined entities. */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Rounds to 0.1 µm and drops the noise, which keeps the package small. */
function num(value: number): string {
  const rounded = Math.round(value * 1e4) / 1e4;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

/**
 * Streams XML into UTF-8 chunks.
 *
 * A dense wall reaches millions of triangles; building the document as one
 * JavaScript string would spike memory far above the finished file. Flushing
 * every few thousand triangles keeps the peak bounded.
 */
class XmlChunkWriter {
  private readonly encoder = new TextEncoder();
  private readonly chunks: Uint8Array[] = [];
  private buffer: string[] = [];
  private pending = 0;

  push(text: string): void {
    this.buffer.push(text);
    this.pending += text.length;
    if (this.pending > 1 << 20) this.flush();
  }

  private flush(): void {
    if (this.buffer.length === 0) return;
    this.chunks.push(this.encoder.encode(this.buffer.join('')));
    this.buffer = [];
    this.pending = 0;
  }

  finish(): Uint8Array {
    this.flush();
    let total = 0;
    for (const chunk of this.chunks) total += chunk.length;

    const result = new Uint8Array(total);
    let offset = 0;
    for (const chunk of this.chunks) {
      result.set(chunk, offset);
      offset += chunk.length;
    }
    return result;
  }
}

export function buildThreeMfModel(
  objects: readonly ThreeMfObject[],
  options: ThreeMfOptions = {},
): Uint8Array {
  const writer = new XmlChunkWriter();

  writer.push('<?xml version="1.0" encoding="UTF-8"?>\n');
  writer.push(
    '<model unit="millimeter" xml:lang="en-US" ' +
      'xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">\n',
  );
  writer.push(
    `  <metadata name="Application">${escapeXml(options.application ?? 'Organic3D')}</metadata>\n`,
  );
  if (options.title) {
    writer.push(`  <metadata name="Title">${escapeXml(options.title)}</metadata>\n`);
  }
  if (options.designer) {
    writer.push(`  <metadata name="Designer">${escapeXml(options.designer)}</metadata>\n`);
  }
  if (options.description) {
    writer.push(`  <metadata name="Description">${escapeXml(options.description)}</metadata>\n`);
  }
  writer.push('  <resources>\n');

  objects.forEach((object, index) => {
    const id = index + 1;
    writer.push(
      `    <object id="${id}" type="model" name="${escapeXml(object.name)}">\n      <mesh>\n        <vertices>\n`,
    );

    const { positions, indices } = object.mesh;
    for (let i = 0; i < positions.length; i += 3) {
      writer.push(
        `          <vertex x="${num(positions[i])}" y="${num(positions[i + 1])}" z="${num(
          positions[i + 2],
        )}"/>\n`,
      );
    }

    writer.push('        </vertices>\n        <triangles>\n');
    for (let i = 0; i < indices.length; i += 3) {
      writer.push(
        `          <triangle v1="${indices[i]}" v2="${indices[i + 1]}" v3="${indices[i + 2]}"/>\n`,
      );
    }
    writer.push('        </triangles>\n      </mesh>\n    </object>\n');
  });

  writer.push('  </resources>\n  <build>\n');
  objects.forEach((object, index) => {
    const id = index + 1;
    const placement = object.placement;
    const moved =
      placement &&
      (placement.x !== 0 || placement.y !== 0 || placement.z !== 0 || placement.rotation !== 0);

    if (moved) {
      // 3MF transforms are 3x4 and row-major, applied as `v * M`, so the basis
      // vectors are the first three rows and the translation is the last.
      const angle = (placement.rotation * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const matrix = [cos, sin, 0, -sin, cos, 0, 0, 0, 1, placement.x, placement.y, placement.z]
        .map(num)
        .join(' ');
      writer.push(`    <item objectid="${id}" transform="${matrix}"/>\n`);
    } else {
      writer.push(`    <item objectid="${id}"/>\n`);
    }
  });
  writer.push('  </build>\n</model>\n');

  return writer.finish();
}

export function exportThreeMf(
  objects: readonly ThreeMfObject[],
  options: ThreeMfOptions = {},
): Uint8Array {
  const encoder = new TextEncoder();
  // Key order is preserved by fflate, and readers expect the content types
  // part first, so it is inserted first.
  return zipSync(
    {
      '[Content_Types].xml': encoder.encode(CONTENT_TYPES),
      '_rels/.rels': encoder.encode(ROOT_RELS),
      [MODEL_PATH]: buildThreeMfModel(objects, options),
    },
    { level: 6 },
  );
}
